import { useState } from "react";
import Sidebar from "../common/Sidebar";
import Header from "../common/Header";
import CreateTemplateModal from "./CreateTemplateModal";
import MessageViewModal from "./MessageViewModal";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import { Eye, FileText, Folder, Hash, Tag, Edit, Trash2 } from "lucide-react";
import { deleteTemplate } from "../../../services/templateServices";
import toast from "react-hot-toast";
import DeleteConfirmationModal from "../members/DeleteMemberModal";

const TemplatesHandler = ({ template }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editData, setEditData] = useState(null);

  const handleEdit = (item) => {
    setEditData(item);
    setOpenModal(true);
  };

  const handleDelete = async () => {
    if (!selected) return;

    toast
      .promise(deleteTemplate(selected._id), {
        loading: "Deleting template...",
        success: "Template deleted successfully!",
        error: (err) => err.message || "Failed to delete template",
      })
      .then(() => {
        setOpenDelete(false);
        setSelected(null);
        window.location.href = window.location.href;
      });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditData(null);
  };

  return (
    <>
      <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
        <div className="h-full">
          <div className="sticky top-0 h-screen text-white">
            <Sidebar />
          </div>
        </div>

        <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
          <PageWrapper>
            <div className="flex flex-col h-full">
              <div className="flex-1 p-4 overflow-y-auto">
                <Header
                  title="Templates"
                  desc="Here we can create and manage templates"
                />

                <section className="flex justify-end w-full mb-6 mt-4">
                  <button
                    onClick={() => setOpenModal(true)}
                    className="bg-[#4F22D8] cursor-pointer border border-[#0000003D] text-white px-4 py-2 text-sm font-semibold rounded-[10px] hover:opacity-90 transition"
                  >
                    + Create Template
                  </button>
                </section>

                <section className="bg-white rounded-xl overflow-hidden border border-[#D5D5D5] mt-10">
                  <div className="overflow-auto">
                    <table className="min-w-full text-sm font-medium text-black table-fixed">
                      <thead className="bg-[#4F22D8] text-white font-medium text-sm">
                        <tr>
                          <th className="px-4 py-4 text-left w-[120px] rounded-tl-lg">
                            <div className="flex items-center gap-2">
                              <Hash className="w-4 h-4" />
                              No
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left w-[140px] border-l border-white/40">
                            <div className="flex items-center gap-2">
                              <Folder className="w-4 h-4" />
                              Group Name
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left w-[180px] border-l border-white/40">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Template Name
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left w-[250px] border-l border-white/40">
                            Sample Content
                          </th>
                          <th className="px-4 py-3 text-left w-[150px] border-l border-white/40">
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4" />
                              Placeholders
                            </div>
                          </th>
                          <th className="px-4 py-3 text-center w-[160px] rounded-tr-lg border-l border-white/40">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {template.map((item, idx) => (
                          <tr
                            key={idx}
                            className="text-sm text-[#696969] font-normal border-b border-[#B0B0B075] hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-3">{idx + 1}</td>
                            <td className="px-4 py-3 border-l border-[#E0E0E0]">
                              {item.templateName}
                            </td>
                            <td className="px-4 py-3 border-l border-[#E0E0E0]">
                              {item.metaTemplateId}
                            </td>

                            <td className="px-4 py-3 whitespace-normal break-words border-l border-[#D5D5D5]">
                              {item.type === "media" && item.mediaUrl && (
                                <div className="flex flex-col items-start gap-2">
                                  {item.mediaUrl.endsWith(".mp4") ||
                                  item.mediaUrl.endsWith(".mov") ||
                                  item.mediaUrl.endsWith(".webm") ? (
                                    <video
                                      src={item.mediaUrl}
                                      controls
                                      preload="metadata"
                                      className="w-16 h-16 object-cover rounded-md"
                                      onError={(e) => {
                                        console.error("Video load error:", e);
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={item.mediaUrl}
                                      alt="Media"
                                      className="w-16 h-16 object-cover rounded-md"
                                      onError={(e) => {
                                        console.error("Image load error:", e);
                                      }}
                                    />
                                  )}
                                  <div className="text-sm text-[#4B4B4B]">
                                    {item.sampleMessage}
                                  </div>
                                </div>
                              )}

                              {item.type === "media" && !item.mediaUrl && (
                                <div className="text-sm text-[#4B4B4B]">
                                  <span className="text-gray-400 italic block">
                                    No media
                                  </span>
                                  {item.sampleMessage && (
                                    <p>{item.sampleMessage}</p>
                                  )}
                                </div>
                              )}

                              {item.type === "text" && item.sampleMessage}
                            </td>
                            <td className="px-4 py-3 border-l border-[#E0E0E0]">
                              {item.placeholders &&
                              item.placeholders.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {item.placeholders.map(
                                    (placeholder, pidx) => (
                                      <span
                                        key={pidx}
                                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                                      >
                                        <span className="font-mono">
                                          {`{{${placeholder.position}}}`}
                                        </span>
                                        <span className="text-xs">
                                          {placeholder.label}
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs italic">
                                  No placeholders
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center border-l border-[#E0E0E0]">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => {
                                    setSelected(item);
                                    setOpenView(true);
                                  }}
                                  className="flex items-center justify-center gap-1 bg-[#E0E3FF] cursor-pointer text-[#1E30D4] px-2 py-1.5 rounded-[8px] font-semibold text-xs shadow-sm hover:bg-[#d0d5ff] transition-colors"
                                >
                                  <Eye className="w-3 h-3" />
                                  View
                                </button>
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="flex items-center justify-center gap-1 bg-[#FFF4E6] cursor-pointer text-[#D4841E] px-2 py-1.5 rounded-[8px] font-semibold text-xs shadow-sm hover:bg-[#FFE6CC] transition-colors"
                                >
                                  <Edit className="w-3 h-3" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setSelected(item);
                                    setOpenDelete(true);
                                  }}
                                  className="flex items-center justify-center gap-1 bg-[#FFE6E6] cursor-pointer text-[#D41E30] px-2 py-1.5 rounded-[8px] font-semibold text-xs shadow-sm hover:bg-[#FFCCCC] transition-colors"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {template.length === 0 && (
                      <div className="py-12 text-center text-gray-500">
                        No templates found
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </PageWrapper>
        </div>
      </div>

      {openModal && (
        <CreateTemplateModal onClose={handleCloseModal} editData={editData} />
      )}

      <MessageViewModal
        isOpen={openView}
        onClose={() => setOpenView(false)}
        data={selected}
      />

      <DeleteConfirmationModal
        isOpen={openDelete}
        onRequestClose={() => setOpenDelete(false)}
        onDelete={handleDelete}
        itemName={selected?.templateName || ""}
        itemType="template"
      />
    </>
  );
};

export default TemplatesHandler;
